import { baseApi } from './baseApi';

// Types
export interface Answer {
  question_id: string;
  answer: string | number | boolean | null;
}

export interface HealthAssessment {
  id?: string;
  user_id?: string;
  assessment_date?: string;
  answers: Answer[];
  overall_health_score?: number;
  notes?: string;
  created_at?: string;
  updated_at?: string;
}

export interface PaginationParams {
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

export interface PaginatedResponse<T> {
  data: T[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

export const healthAssessmentApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({

    // ⭐ Get all health assessments (paginated)
    getHealthAssessments: builder.query<
      PaginatedResponse<HealthAssessment>,
      PaginationParams
    >({
      query: ({
        page = 1,
        limit = 10,
        sortBy = 'created_at',
        sortOrder = 'desc',
      }) => ({
        url: `/assessment`,
        method: 'GET',
        params: { page, limit, sortBy, sortOrder },
      }),
      providesTags: ['HealthAssessment'],
    }),

    // ⭐ Get single assessment by ID
    getHealthAssessmentById: builder.query<HealthAssessment, string>({
      query: (id) => ({
        url: `/assessment/${id}`,
        method: 'GET',
      }),
      providesTags: ['HealthAssessment'],
    }),

    // ⭐ Create assessment
    createHealthAssessment: builder.mutation<
      HealthAssessment,
      Omit<HealthAssessment, 'id' | 'created_at' | 'updated_at'>
    >({
      query: (assessment) => ({
        url: `/assessment/create`,
        method: 'POST',
        body: assessment,
      }),
      invalidatesTags: ['HealthAssessment'],
    }),

    // ⭐ Update assessment
    updateHealthAssessment: builder.mutation<
      HealthAssessment,
      { id: string } & Partial<HealthAssessment>
    >({
      query: ({ id, ...updates }) => ({
        url: `/assessment/${id}`,
        method: 'PUT',
        body: updates,
      }),
      invalidatesTags: ['HealthAssessment'],
    }),

    // ⭐ Delete assessment
    deleteHealthAssessment: builder.mutation<void, string>({
      query: (id) => ({
        url: `/assessment/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['HealthAssessment'],
    }),

    // ⭐ Get assessments for a user (paginated)
    getHealthAssessmentsByUserId: builder.query<
      PaginatedResponse<HealthAssessment>,
      { userId: string } & PaginationParams
    >({
      query: ({
        userId,
        page = 1,
        limit = 10,
        sortBy = 'created_at',
        sortOrder = 'desc',
      }) => ({
        url: `/assessment/user/${userId}`,
        method: 'GET',
        params: { page, limit, sortBy, sortOrder },
      }),
      providesTags: ['HealthAssessment'],
    }),

    // ⭐ Get latest assessment for user
    getLatestHealthAssessment: builder.query<HealthAssessment, string>({
      query: (userId) => ({
        url: `/assessment/user/${userId}/latest`,
        method: 'GET',
      }),
      providesTags: ['HealthAssessment'],
    }),

  }),

  overrideExisting: false,
});

// Hooks
export const {
  useGetHealthAssessmentsQuery,
  useGetHealthAssessmentByIdQuery,
  useCreateHealthAssessmentMutation,
  useUpdateHealthAssessmentMutation,
  useDeleteHealthAssessmentMutation,
  useGetHealthAssessmentsByUserIdQuery,
  useGetLatestHealthAssessmentQuery,
} = healthAssessmentApi;
